import { el, clear } from "../lib/dom";
import { getProgress, getCosmetics, ownsCosmetic, buyCosmetic, equipCosmetic, subscribe } from "../lib/storage";
import { getPalette } from "../lib/palette";
import { SHOP_ITEMS, type ShopItem } from "../shop/catalog";
import { modal, toast } from "./components";
import { navigate } from "./router";
import type { Screen } from "./home";

export function shopScreen(): Screen {
  const coinsEl = el("span.coins");
  const header = el(
    "header.app-header",
    {},
    el("button.icon-btn", { onclick: () => navigate("/"), "aria-label": "Terug" }, "‹"),
    el("div.level-header-title", {}, "Shop"),
    coinsEl,
  );

  const palettes = SHOP_ITEMS.filter((i) => i.type === "palette");
  const backgrounds = SHOP_ITEMS.filter((i) => i.type === "background");
  const mascots = SHOP_ITEMS.filter((i) => i.type === "mascot");

  const paletteList = el("div.shop-list");
  const backgroundList = el("div.shop-list");
  const mascotList = el("div.shop-list");

  const node = el(
    "div.screen.shop-screen",
    {},
    header,
    el("h2.section-title", {}, "🎨 Blokkleuren"),
    paletteList,
    el("h2.section-title", {}, "🖼️ Achtergronden"),
    backgroundList,
    el("h2.section-title", {}, "🐾 Mascottes"),
    mascotList,
    el("p.footer-note", {}, "Verdien munten door levels voor het eerst uit te spelen."),
  );

  // ---- Previews ----
  function palettePreview(id: string): HTMLElement {
    const row = el("div.shop-swatches");
    const colors = getPalette(id).colors;
    for (let i = 0; i < 6; i++) {
      const dot = el("span.shop-swatch");
      dot.style.background = colors[i];
      row.append(dot);
    }
    return row;
  }

  function backgroundPreview(id: string): HTMLElement {
    return el(`div.bg-prev.bg-prev-${id}`);
  }

  function mascotPreview(item: ShopItem): HTMLElement {
    if (item.svg) {
      const tile = el("div.mascot-prev.mascot-prev-svg");
      const cat = el("span.mascot-svg");
      cat.innerHTML = item.svg;
      tile.append(cat);
      return tile;
    }
    return el("div.mascot-prev", {}, item.emoji ?? "😴");
  }

  // ---- One item card ----
  function card(item: ShopItem): HTMLElement {
    const equipped = getCosmetics().equipped[item.type] === item.id;
    const owned = ownsCosmetic(item.id);
    const coins = getProgress().coins;

    const preview =
      item.type === "palette"
        ? palettePreview(item.id)
        : item.type === "background"
          ? backgroundPreview(item.id)
          : mascotPreview(item);

    let action: HTMLElement;
    if (equipped) {
      action = el("span.shop-badge", {}, "✓ Actief");
    } else if (owned) {
      action = el(
        "button.shop-btn.use",
        {
          onclick: () => {
            equipCosmetic(item.type, item.id);
            toast(`${item.name} in gebruik`);
          },
        },
        "Gebruik",
      );
    } else if (coins >= item.price) {
      action = el(
        "button.shop-btn.buy",
        {
          onclick: () => {
            const close = modal({
              title: `${item.name} kopen?`,
              body: `Koop ${item.name} voor 🪙 ${item.price}?`,
              buttons: [
                { label: "Annuleren", onClick: () => close() },
                {
                  label: "Kopen",
                  primary: true,
                  onClick: () => {
                    const ok = buyCosmetic(item.id, item.price, item.type);
                    close();
                    toast(ok ? "Gekocht en meteen actief!" : "Niet genoeg munten");
                  },
                },
              ],
            });
          },
        },
        `🪙 ${item.price}`,
      );
    } else {
      action = el("button.shop-btn.buy", { disabled: true }, `🪙 ${item.price}`);
    }

    return el(
      `div.shop-card${equipped ? ".equipped" : ""}`,
      {},
      preview,
      el(
        "div.shop-info",
        {},
        el("div.shop-name", {}, item.name),
        item.price === 0
          ? el("div.shop-desc", {}, "Gratis")
          : item.description
            ? el("div.shop-desc", {}, item.description)
            : null,
      ),
      action,
    );
  }

  function render(): void {
    coinsEl.textContent = `🪙 ${getProgress().coins}`;
    clear(paletteList);
    for (const i of palettes) paletteList.append(card(i));
    clear(backgroundList);
    for (const i of backgrounds) backgroundList.append(card(i));
    clear(mascotList);
    for (const i of mascots) mascotList.append(card(i));
  }

  // Re-render on any storage change (purchase, equip, coins, remote sync).
  const unsub = subscribe(() => render());

  return {
    node,
    destroy() {
      unsub();
    },
  };
}
